import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { useListings } from "../hooks/useListings";
import { ListingCard } from "../components/ListingCard";
import { StarRatingDisplay } from "../components/StarRating";
import { SellerBadges } from "../components/SellerBadges";
import { EmptyState, ErrorBanner, LoadingState } from "../components/States";
import { formatDateTime } from "../lib/format";
import { usePageMeta, useJsonLd, breadcrumbJsonLd, SITE_URL } from "../lib/seo";

export function SellerProfilePage() {
  const { id } = useParams<{ id: string }>();

  const { data: seller, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.sellerProfile(id ?? ""),
    queryFn: ({ signal }) => apiClient.getSellerProfile(id as string, signal),
    enabled: Boolean(id),
  });

  const { data: listings, isLoading: isLoadingListings } = useListings({ sellerId: id });

  usePageMeta({
    title: seller?.username ? `${seller.username}'s listings` : "Seller",
    description: seller?.username
      ? `Browse digital goods for sale by ${seller.username} on MarketOnBase.`
      : "A seller's storefront on MarketOnBase.",
    path: `/sellers/${id ?? ""}`,
    // A 404/suspended seller (see services/sellers.ts) shouldn't stay indexable under a generic
    // "Seller" title.
    index: !isError,
  });

  useJsonLd(
    useMemo(
      () =>
        seller
          ? breadcrumbJsonLd([
              { name: "Browse listings", path: "/" },
              { name: seller.username ?? "Seller", path: `/sellers/${id ?? ""}` },
            ])
          : null,
      [seller, id],
    ),
  );

  useJsonLd(
    useMemo(
      () =>
        seller?.username
          ? {
              "@context": "https://schema.org",
              "@type": "ProfilePage",
              url: `${SITE_URL}/sellers/${id ?? ""}`,
              mainEntity: {
                "@type": "Person",
                name: seller.username,
                url: `${SITE_URL}/sellers/${id ?? ""}`,
              },
            }
          : null,
      [seller, id],
    ),
  );

  if (isLoading) return <LoadingState label="Loading seller…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!seller) return null;

  return (
    <div className="stack">
      <div className="card stack">
        <h1>{seller.username ?? "Seller"}</h1>
        <p className="text-sm muted">Member since {formatDateTime(seller.memberSince)}</p>
        <StarRatingDisplay rating={seller.sellerRating} />
        <SellerBadges badges={seller.sellerBadges} />
      </div>

      <h2>Listings</h2>
      {isLoadingListings && <LoadingState label="Loading listings…" />}
      {listings && listings.items.length === 0 && (
        <EmptyState title="Nothing for sale right now">
          <p>This seller doesn't have any published listings at the moment.</p>
        </EmptyState>
      )}
      {listings && listings.items.length > 0 && (
        <div className="grid grid--listings">
          {listings.items.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      )}
    </div>
  );
}
